import { useEffect, useRef, useState } from "react";

import { GoogleMapsService } from "../../../services/GoogleMapsService";

type TGoogleMapsProps = {
	lat: number;
	lng: number;
	zoom?: number;
	className: string;
	markerType?: "fixed" | "centering";
	onMarkerPositionChange?: (lat: number, lng: number) => void;
};

export function GoogleMaps({
	lat = 0,
	lng = 0,
	zoom = 18,
	className,
	markerType = "fixed",
	onMarkerPositionChange,
}: TGoogleMapsProps) {
	const mapRef = useRef<google.maps.Map | null>(null);
	const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
		null,
	);
	const [mapsZoom, setMapsZoom] = useState(zoom);

	useEffect(() => {
		setMapsZoom(zoom);
	}, [zoom]);

	useEffect(() => {
		const initMap = async () => {
			const { Map } = await GoogleMapsService.loadMaps();
			const { AdvancedMarkerElement } = await GoogleMapsService.loadMarker();

			const mapElement = document.getElementById("map");

			if (!mapElement) {
				return;
			}

			if (!mapRef.current) {
				mapRef.current = new Map(mapElement, {
					center: { lat: lat ?? 0, lng: lng ?? 0 },
					zoom: mapsZoom,
					mapId: "google-map-id",
				});
			} else {
				mapRef.current.setCenter({ lat, lng });
				mapRef.current.setZoom(mapsZoom);
			}

			if (!markerRef.current) {
				markerRef.current = new AdvancedMarkerElement({
					map: mapRef.current,
					position: { lat: lat ?? 0, lng: lng ?? 0 },
				});
			} else {
				markerRef.current.position = { lat, lng };
			}

			mapRef.current.addListener("center_changed", () => {
				if (markerType === "centering" && markerRef.current) {
					const newLat = mapRef.current?.getCenter()?.lat() ?? 0;
					const newLng = mapRef.current?.getCenter()?.lng() ?? 0;
					markerRef.current.position = { lat: newLat, lng: newLng };
				}
			});

			mapRef.current.addListener("dragend", () => {
				if (markerType === "centering") {
					const newLat = mapRef.current?.getCenter()?.lat() ?? 0;
					const newLng = mapRef.current?.getCenter()?.lng() ?? 0;
					onMarkerPositionChange?.(newLat, newLng);
				}
			});

			mapRef.current.addListener("zoom_changed", () => {
				setMapsZoom(mapRef.current?.getZoom() ?? 18);
			});
		};

		initMap();
	}, [lat, lng, markerType, onMarkerPositionChange, mapsZoom]);

	return <div id="map" className={className} />;
}
